import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Search, SlidersHorizontal, Grid3X3, LayoutList, ChevronDown } from 'lucide-react';
import ProductCard from '../../components/molecules/ProductCard/ProductCard';

const Shop = () => {
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('featured');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [priceRange, setPriceRange] = useState([0, 100]);

  const categories = [
    { id: 'all', name: 'All Products', count: 5000 },
    { id: 'rice', name: 'Rice & Grains', count: 120 },
    { id: 'vegetables', name: 'Fresh Vegetables', count: 80 },
    { id: 'spices', name: 'Spices & Masala', count: 200 },
    { id: 'tea', name: 'Tea & Coffee', count: 45 },
    { id: 'oils', name: 'Cooking Oils', count: 30 },
    { id: 'snacks', name: 'Snacks & Sweets', count: 150 },
  ];

  const products = [
    { id: 1, name: 'Ponni Boiled Rice', brand: 'Chozha', price: 16.66, weight: '10kg', image: 'https://images.unsplash.com/photo-1586201375761-83865001e31c?w=400', rating: 4.8, reviews: 234, tag: 'Best Seller' },
    { id: 2, name: 'Idli Rice', brand: 'Chozha', price: 17.49, weight: '10kg', image: 'https://images.unsplash.com/photo-1586201375761-83865001e31c?w=400', rating: 4.9, reviews: 189, tag: 'Premium' },
    { id: 3, name: 'Jeera Samba Rice', brand: 'Chozha', price: 16.65, weight: '5kg', image: 'https://images.unsplash.com/photo-1586201375761-83865001e31c?w=400', rating: 4.7, reviews: 156, tag: null },
    { id: 4, name: 'Sona Masoori Rice', brand: 'Chozha', price: 17.26, weight: '10kg', image: 'https://images.unsplash.com/photo-1586201375761-83865001e31c?w=400', rating: 4.6, reviews: 312, tag: 'Popular' },
    { id: 5, name: 'Atta Multigrains', brand: 'Aashirvaad', price: 9.82, weight: '5kg', image: 'https://images.unsplash.com/photo-1627485937980-221c88ac04f9?w=400', rating: 4.5, reviews: 98, tag: null },
    { id: 6, name: 'Tata Salt', brand: 'Tata', price: 1.09, weight: '1kg', image: 'https://images.unsplash.com/photo-1474979266404-7eaacbcd87c5?w=400', rating: 4.9, reviews: 567, tag: 'Essential' },
    { id: 7, name: 'Sesame Oil', brand: 'Idhayam', price: 5.85, weight: '1L', image: 'https://images.unsplash.com/photo-1474979266404-7eaacbcd87c5?w=400', rating: 4.7, reviews: 145, tag: null },
    { id: 8, name: 'Ponni Boiled Rice', brand: 'Annam', price: 8.37, weight: '5kg', image: 'https://images.unsplash.com/photo-1586201375761-83865001e31c?w=400', rating: 4.4, reviews: 203, tag: 'Value' },
    { id: 9, name: 'Red Label Tea', brand: 'Brooke Bond', price: 3.99, weight: '500g', image: 'https://images.unsplash.com/photo-1544787219-7f47ccb76574?w=400', rating: 4.8, reviews: 421, tag: 'Best Seller' },
    { id: 10, name: 'BRU Coffee', brand: 'HUL', price: 4.49, weight: '200g', image: 'https://images.unsplash.com/photo-1544787219-7f47ccb76574?w=400', rating: 4.6, reviews: 312, tag: null },
    { id: 11, name: 'Aashirvaad Atta', brand: 'ITC', price: 7.99, weight: '5kg', image: 'https://images.unsplash.com/photo-1627485937980-221c88ac04f9?w=400', rating: 4.7, reviews: 892, tag: 'Popular' },
    { id: 12, name: 'Gold Winner Oil', brand: 'Gold Winner', price: 8.99, weight: '5L', image: 'https://images.unsplash.com/photo-1474979266404-7eaacbcd87c5?w=400', rating: 4.5, reviews: 234, tag: null },
  ];

  const filteredProducts = products.filter(p => {
    const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          p.brand.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesPrice = p.price >= priceRange[0] && p.price <= priceRange[1];
    return matchesSearch && matchesPrice;
  });

  return (
    <div className="min-h-screen bg-gray-50 pt-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-4xl font-bold text-gray-900">Shop All Products</h1>
          <p className="text-gray-500 mt-2">Discover our wide range of authentic Indian groceries</p>
        </motion.div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Filters */}
          <motion.aside
            className="lg:w-64 flex-shrink-0"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 sticky top-24">
              <div className="flex items-center gap-2 mb-6">
                <SlidersHorizontal size={20} className="text-primary-600" />
                <h3 className="font-bold text-lg">Filters</h3>
              </div>

              {/* Search */}
              <div className="mb-6">
                <label className="text-sm font-medium text-gray-700 mb-2 block">Search</label>
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search products..."
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:border-primary-500 text-sm"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>

              {/* Categories */}
              <div className="mb-6">
                <label className="text-sm font-medium text-gray-700 mb-3 block">Categories</label>
                <div className="space-y-2">
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
                        selectedCategory === cat.id
                          ? 'bg-primary-50 text-primary-600 font-medium'
                          : 'hover:bg-gray-50 text-gray-600'
                      }`}
                      onClick={() => setSelectedCategory(cat.id)}
                    >
                      <span>{cat.name}</span>
                      <span className="text-xs bg-gray-100 px-2 py-0.5 rounded-full">{cat.count}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Price Range */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-3 block">Price Range</label>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">£{priceRange[0]}</span>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={priceRange[1]}
                    onChange={(e) => setPriceRange([0, parseInt(e.target.value)])}
                    className="flex-1 accent-primary-500"
                  />
                  <span className="text-sm text-gray-500">£{priceRange[1]}</span>
                </div>
              </div>
            </div>
          </motion.aside>

          {/* Products */}
          <div className="flex-1">
            {/* Toolbar */}
            <div className="flex items-center justify-between mb-6 bg-white p-4 rounded-2xl shadow-sm border border-gray-100">
              <p className="text-sm text-gray-500">
                Showing <span className="font-bold text-gray-900">{filteredProducts.length}</span> products
              </p>
              <div className="flex items-center gap-4">
                <div className="relative">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="appearance-none bg-gray-50 border border-gray-200 rounded-lg px-4 py-2 pr-8 text-sm focus:outline-none focus:border-primary-500"
                  >
                    <option value="featured">Featured</option>
                    <option value="price-low">Price: Low to High</option>
                    <option value="price-high">Price: High to Low</option>
                    <option value="rating">Top Rated</option>
                  </select>
                  <ChevronDown size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                </div>
                <div className="flex gap-1">
                  <button
                    className={`p-2 rounded-lg transition-colors ${viewMode === 'grid' ? 'bg-primary-100 text-primary-600' : 'hover:bg-gray-100 text-gray-400'}`}
                    onClick={() => setViewMode('grid')}
                  >
                    <Grid3X3 size={18} />
                  </button>
                  <button
                    className={`p-2 rounded-lg transition-colors ${viewMode === 'list' ? 'bg-primary-100 text-primary-600' : 'hover:bg-gray-100 text-gray-400'}`}
                    onClick={() => setViewMode('list')}
                  >
                    <LayoutList size={18} />
                  </button>
                </div>
              </div>
            </div>

            {/* Product Grid */}
            <div className={`grid gap-6 ${viewMode === 'grid' ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'}`}>
              {filteredProducts.map((product, index) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <ProductCard product={product} />
                </motion.div>
              ))}
            </div>

            {filteredProducts.length === 0 && (
              <div className="text-center py-20">
                <p className="text-gray-500 text-lg">No products found matching your criteria.</p>
                <button
                  className="mt-4 text-primary-600 font-medium hover:underline"
                  onClick={() => { setSearchQuery(''); setPriceRange([0, 100]); }}
                >
                  Clear all filters
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Shop;
